import { inject, Navigator } from "fw";
import { Store, handle, dispatch } from "fw-state";
import { wait } from "wait";
import { resetAllOnce } from "helpers/once";
import { LogoutAction, StartAction, RefreshTokenAction } from "./actions";
import { InitActivityTrackerAction, StopActivityTrackerAction, ToggleActivityAlert } from "state/activity";
import { UserRepository } from "network/user-repository";
import { User } from "models/user";
import { LoginForm } from "forms/login";
import { FormErrorHandling } from "./error-handling";
import { Manager } from "network/manager";
import { AtsLoginForm } from "forms/ats-login";
import { AtsRepository } from "network/ats-repository";
import { UpdatedUserAction } from "forms/user";
import { ActivityStore } from "state/activity";
import { dateDiff } from "helpers/date-diff";

type CurrentUserStoreShape = {
    loggedIn: boolean;
    token: string;
    user: User;
    logoutReason: string;
    timezone: string;
};

export class SetTimezoneAction {
    constructor(public timezone: string) {}
}

export class UserTimezoneChangedAction {
    constructor(public timezone: string) {}
}

@inject
export class CurrentUserStore extends Store<CurrentUserStoreShape> {
    private intervalTimer = null;

    constructor(
        private managerRepo: Manager,
        private userRepo: UserRepository,
        private atsRepo: AtsRepository,
        private activityStore: ActivityStore,
        private nav: Navigator
    ) {
        super();
    }

    defaultState() {
        return {
            loggedIn: false,
            token: null,
            user: null,
            logoutReason: null,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
    }

    @handle(StartAction)
    private handleStart(action: StartAction) {
        this.setState((state) => ({
            ...this.defaultState(),
            loggedIn: true,
            token: action.token,
            user: action.user
        }));

        this.startIdleTracking();
    }

    @handle(LogoutAction)
    private handleLogout(action: LogoutAction) {
        this.stopIdleTracking();

        this.setState((state) => ({
            ...this.defaultState(),
            logoutReason: action.reason
        }));

        this.userRepo.logout();

        if (action.redirectToLogin) {
            this.nav.navigate("login");
        }
    }

    @handle(SetTimezoneAction)
    private handleSetTimezoneAction(action: SetTimezoneAction) {
        this.setState((state) => ({
            ...state,
            timezone: action.timezone
        }));

        dispatch(new UserTimezoneChangedAction(action.timezone));
    }

    stopIdleTracking() {
        clearInterval(this.intervalTimer);
        dispatch(new StopActivityTrackerAction());
    }

    private startIdleTracking() {
        const MINUTE = 1000 * 60;
        const RENEWAL_BUFFER = MINUTE * 2; // how long before token expiration do we renew/remind
        let ACTIVITY_IDLE_TIME = MINUTE * 9; // inactivity duration before considered idle
        // because the activity tracker uses setTimeout to measure idle time, laptop sleep does not count toward idle time

        if (this.managerRepo.tokenLifespan > 0) {
            // when session lifespan is overriden for testing, use a short idle time
            ACTIVITY_IDLE_TIME = MINUTE * 0.5;
            console.log(
                `manager-bearer-token-lifespan is set to ${this.managerRepo.tokenLifespan}; Idle time reduced to ${ACTIVITY_IDLE_TIME / 1000} seconds.`
            );
        }

        clearInterval(this.intervalTimer);
        this.intervalTimer = setInterval(async () => await this.checkTimes(RENEWAL_BUFFER), 15000);

        dispatch(new InitActivityTrackerAction(ACTIVITY_IDLE_TIME));
    }

    private async checkTimes(renewalBuffer) {
        // get token expiration from local storage (shared with other tabs)
        const tokenExpDate = this.managerRepo.getTokenExpDate();
        if (!tokenExpDate) {
            await dispatch(new LogoutAction("You were automatically logged out."));
            this.nav.navigate("login");
            return;
        }

        const timeLeft = dateDiff(new Date(), tokenExpDate);
        if (timeLeft < 0) {
            // we missed the renewal window, probably because of laptop sleep
            console.log("Session time elapsed without a renewal.");
            await dispatch(new LogoutAction("You were automatically logged out due to inactivity."));
            this.nav.navigate("login");
        } else if (timeLeft > renewalBuffer) {
            // do nothing; token still has time left
            return;
        } else if (this.activityStore.state.showActivityAlert) {
            // do nothing; alert is already showing
            return;
        } else if (this.activityStore.state.isIdle) {
            // user is idle and token is about to expire; show the alert
            await dispatch(new ToggleActivityAlert(true));
        } else {
            // user is active and token is about to expire; renew it now
            console.log(`Token refresh requested at ${new Date()}`);
            await dispatch(new RefreshTokenAction());
        }
    }

    @handle(RefreshTokenAction)
    private async handleRefreshToken() {
        dispatch(new ToggleActivityAlert(false));
        await this.userRepo.refreshToken();
    }

    @handle(LoginForm, FormErrorHandling)
    private async handleLoginForm(loginForm: LoginForm) {
        loginForm.validate();

        const { EmailAddress, Password, inviteToken, Code } = loginForm;
        const token = await this.userRepo.login({
            EmailAddress,
            Password,
            Token: inviteToken,
            Code
        });

        if (inviteToken != null) {
            await wait(2000);
        }

        const user = await this.managerRepo.currentUser();
        // reset the lazy load tracking for all states
        resetAllOnce();
        await dispatch(new StartAction(user, token));
    }

    @handle(AtsLoginForm, FormErrorHandling)
    private async handleAtsLoginForm(atsLoginForm: AtsLoginForm) {
        atsLoginForm.validate();

        try {
            const ticket = await this.atsRepo.atsLogin(atsLoginForm.organizationId, atsLoginForm.impersonationOrganizationUserId);
            const organization = await this.atsRepo.organizationById(atsLoginForm.organizationId);
            window.open(`https://${organization.Domain}/#/ticket=${ticket}`, "_blank").focus();
        } catch {
            console.error("Something went wrong with the token retrieval.");
        }
    }

    @handle(UpdatedUserAction)
    private handleUpdatedUser(action: UpdatedUserAction) {
        if (action.user.Id == this.state.user.Id) {
            this.setState((state) => ({ ...state, user: action.user }));
        }
    }
}
